import { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { InputText, InputSelect, CheckBox } from "@2solve/tosolve-inputs";
import useFormValidation from "../../../hooks/useFormValidation";
import handleInputChange from "../../../hooks/inputChange";

function AtributosModal({
  show,
  onHide,
  isEdit,
  editingAttribute,
  onDataEdited,
  error,
  setError,
  categories = [],
  postData,
  updateData,
  onDataAdded,
}) {
  const { t } = useTranslation();
  const isButtonDisabled = useFormValidation();

  const [formData, setFormData] = useState({});
  const [initialData, setInitialData] = useState({});

  const closeModal = () => {
    setFormData({});
    setError("");
    onHide();
  };

  useEffect(() => {
    if (isEdit && editingAttribute) {
      const initialValues = {
        name: editingAttribute.name || "",
        data_type: editingAttribute.data_type || "",
        description: editingAttribute.description || "",
        resource_id: editingAttribute.resource
          ? editingAttribute.resource.id
          : null,
        is_required: editingAttribute.is_required || false,
        slug: editingAttribute.slug || false,
      };
      setFormData(initialValues);
      setInitialData(initialValues);
    } else {
      setFormData({
        name: "",
        data_type: "",
        resource_id: null,
        is_required: false,
        description: null,
      });
      setInitialData({});
    }
  }, [isEdit, editingAttribute, show]);

  const typeOptions = [
    { value: "S", label: t("Texto") },
    { value: "N", label: t("Numérico") },
    { value: "B", label: t("Booleano") },
  ];

  const resourceOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      setError(t("O campo Nome é obrigatório."));
      return;
    }
    if (!formData.data_type) {
      setError(t("O campo Tipo é obrigatório."));
      return;
    }
    if (!formData.resource_id) {
      setError(t("O campo Recurso é obrigatório."));
      return;
    }

    if (!isEdit) {
      try {
        const response = await postData("/attributes", formData);

        const newAttribute = response.data;
        onDataAdded(newAttribute);
        closeModal();
      } catch (err) {
        console.error(err);
        setError(t("Erro ao cadastrar atributo"));
      }
    } else {
      const modified = {};
      Object.keys(formData).forEach((k) => {
        if (formData[k] !== initialData[k]) modified[k] = formData[k];
      });
      if (Object.keys(modified).length === 0) {
        setError(t("Nenhuma alteração foi feita"));
        return;
      }

      try {
        await updateData(`/attributes/${editingAttribute.id}`, modified);
        onDataEdited();
        closeModal();
      } catch (err) {
        console.error(err);
        setError(t("Erro ao editar atributo"));
      }
    }
  };

  return (
    <Modal backdrop="static" size="lg" centered show={show} onHide={closeModal}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {isEdit ? t("Editar atributo") : t("Cadastrar atributo")}
        </Modal.Title>
      </Modal.Header>
      {(formData.resource_id || !isEdit) && (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <InputText
              id="name"
              type="text"
              placeholder={t("Nome")}
              required={true}
              maxLength={80}
              value={formData?.name}
              onChange={handleInputChange(setFormData)}
            />
            <InputText
              className="mt-4"
              id="slug"
              type="text"
              placeholder={t("Nome simplificado (slug)")}
              required={true}
              maxLength={80}
              value={formData?.slug}
              onChange={handleInputChange(setFormData)}
            />
            <InputText
              className="mt-4"
              id="description"
              type="text"
              placeholder={t("Descrição")}
              required={false}
              value={formData?.description}
              onChange={handleInputChange(setFormData)}
            />

            <InputSelect
              className="mt-4"
              placeholder={t("Tipo")}
              id="data_type"
              type="select"
              options={typeOptions}
              required={true}
              isMultiple={false}
              selectAll={false}
              closeOnSelect={true}
              IsClearable={false}
              disabled={isEdit}
              onChange={handleInputChange(setFormData)}
              value={formData.data_type}
            />

            <InputSelect
              className="mt-4"
              placeholder={t("Recurso")}
              id="resource_id"
              type="select"
              options={resourceOptions}
              required={true}
              isMultiple={false}
              selectAll={false}
              closeOnSelect={true}
              IsClearable={false}
              disabled={isEdit}
              onChange={handleInputChange(setFormData)}
              value={formData.resource_id}
            />

            <CheckBox
              id="is_required"
              placeholder={t("Atributo obrigatório")}
              onChange={handleInputChange(setFormData)}
              value={formData.is_required}
              disabled={isEdit}
            />

            {error && <div className="text-danger">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button disabled={isButtonDisabled} type="submit" variant="primary">
              {isEdit ? t("Salvar") : t("Cadastrar")}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}

AtributosModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  isEdit: PropTypes.bool,
  editingAttribute: PropTypes.object,
  onDataAdded: PropTypes.func,
  onDataEdited: PropTypes.func,
  error: PropTypes.string,
  setError: PropTypes.func,
  categories: PropTypes.array,
  postData: PropTypes.func,
  updateData: PropTypes.func,
  fetchData: PropTypes.func,
  onDataAdded: PropTypes.func,
};

export default AtributosModal;
